import { gql, useMutation, type FetchResult } from "@apollo/client";

import { useTimeZoneFromLocationId } from "../../../common/hooks/useTimeZoneFromLocationId";
import { GET_SERVICES_BY_NODE } from "../../../common/operations/queries";
import type {
  AddServiceInput,
  AddServiceMutation,
  AddServiceMutationVariables,
  GetServicesByNodeQuery,
} from "./../../../API";
import { ADD_SERVICE } from "./../../../common/operations/mutations";
import { useSyncShadow } from "./useSyncShadow";

interface CreateServiceInterface {
  createService: (
    input: Omit<AddServiceInput, "timeZone">
  ) => Promise<FetchResult<AddServiceMutation>>;
  data?: AddServiceMutation | null;
  loading: boolean;
}

export const useCreateService = (): CreateServiceInterface => {
  const { syncShadowsForNode } = useSyncShadow();

  const [saveService, { data, loading }] = useMutation<
    AddServiceMutation,
    AddServiceMutationVariables
  >(ADD_SERVICE);

  const createService = async (
    input: Omit<AddServiceInput, "timeZone">
  ): Promise<FetchResult<AddServiceMutation>> => {
    const timeZone = useTimeZoneFromLocationId(input.locationId);

    return await saveService({
      variables: {
        input: {
          ...input,
          timeZone,
        },
      },
      update(cache, response): void {
        const newService = response.data?.addService;

        if (!newService) return;

        const queryData = cache.readQuery<GetServicesByNodeQuery>({
          query: GET_SERVICES_BY_NODE,
          variables: { nodeId: newService.nodeId },
        });

        if (queryData) {
          cache.writeQuery({
            query: GET_SERVICES_BY_NODE,
            variables: { nodeId: newService.nodeId },
            data: {
              getServicesByNode: {
                ...queryData.getServicesByNode,
                items: [...queryData.getServicesByNode.items, newService],
              },
            },
          });
        }

        // Also update getServices cache (used by useGetServicesByLocation)
        cache.modify({
          fields: {
            getServices(existing) {
              const newServiceRef = cache.writeFragment({
                data: newService,
                fragment: gql`
                  fragment NewService on Service {
                    configuration
                    customerId
                    deviceId
                    healthData
                    id
                    isRunning
                    locationId
                    nodeId
                    serviceType
                    modelAlertSettingsId
                  }
                `,
              });

              if (!existing?.items) {
                return { items: [newServiceRef], nextToken: null };
              }

              return {
                ...existing,
                items: [...existing.items, newServiceRef],
              };
            },
          },
        });

        syncShadowsForNode(input.nodeId);
      },
      refetchQueries: ["GetDeviceById", "GetServices"],
    });
  };

  return { createService, data, loading };
};
